import React, { FC, ReactNode, useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import {
  HomePage,
  Instances,
  Volumes,
  Images,
  SSHKeys,
  Networks,
  FloatingIPs,
  SecurityGroups,
  LoadBalancers,
  AccountSettings,
  Clients,
  AccessDenied,
  NotFound,
  CompleteRegistration,
  Routers,
  Operations,
  BillingPage,
  SupportPage,
  CreateTicket,
  TicketPage,
  Notifications,
  Kubernetes,
  CreateCluster,
  ClusterPage,
  LbPool,
  LbListener,
  VolumeBackups,
  BackupPage,
  Quotas,
  BareMetal,
  CreateBareMetal,
  BareMetalPage,
  ObjectStoragePage,
  CreateBucket,
  CreateBucketPolicy,
  BucketPage,
  CreateBucketRule,
  GpusPage,
  CreateAndEditGpuServer,
  Affiliate,
} from '../pages'
import NotificationPage from '../pages/Notifications/notification-page';
import {
  CreateMember,
  CreateMonitor,
  MonitorPage,
  MemberPage,
  EditMember,
  EditPool,
 } from '../pages/lb-pool/components';
import {CreateAndEditInstance, InstancePage, } from '../pages/Instances/components'
import { SideBar, Header, RouteGuard } from '../components';
import { useUi } from '../context/ui.context';
import {PageWrapper} from './styles'
import CreateAndEditVolume from '../pages/Volumes/create-volume';
import VolumePage from '../pages/Volumes/volume-page';
import CreateAndEditImage from '../pages/Images/create-image';
import ImagePage from '../pages/Images/image-page';
import Ports from '../pages/Ports';
import CreatePort from '../pages/Ports/create-port';
import CreateSSH from '../pages/Ssh-keys/create-page';
import CreateNetwork from '../pages/networks/create-network';
import NetworkPage from '../pages/networks/network-page';
import CreateFloatingIp from '../pages/Floating-IPs/create-page';
import CreateSecurityGroup from '../pages/Security-groups/create-page';
import GroupPage from '../pages/Security-groups/group-page';
import CreateRule from '../pages/Security-groups/group-page/create-rule';
import CreateLoadBalancer from '../pages/Load-balancers/create-page';
import ImportSSHKey from '../pages/Ssh-keys/import-page';
import ClientPage from '../pages/Clients/client-page';
import EditClient from '../pages/Clients/client-edit';
import EditSSh from '../pages/Ssh-keys/edit-ssh';
import RouterPage from '../pages/Routers/router-page';
import OperationPage from '../pages/Operations/operation-page';
import EditImage from '../pages/Images/edit-image';
import { InvoicePage, ServicePage } from '../pages/Billing/components';
import EditLB from '../pages/Load-balancers/edit-page';
import BalancerPage from '../pages/Load-balancers/lb-page';
import { CreatePolicy, CreatePolicyRule, EditListener, PolicyPage, RulePolicyPage } from '../pages/lb-listener/components';
import PortPage from '../pages/Ports/port-page';
import CreateNodePool from '../pages/Kubernetes/edit-node_pool';
import { GpuContextProvider } from '../context/gpuContext';
import { ServerPage } from '../pages/gpu/components';
import CreateGpuScript from '../pages/gpu/components/create-scripts';
import { routePermissionSurfaces } from '../permissions';
import { OBJECT_STORAGE_SERVICE_REQUIRED_EVENT } from '../utils/object-storage-service';
import { DEDICATED_SERVICE_REQUIRED_EVENT } from '../utils/dedicated-service';


const GpuRouteCreate = () => {
  return(
    <GpuContextProvider>
      <CreateAndEditGpuServer/>
    </GpuContextProvider>
  )
}


const withRouteGuard = (surface: typeof routePermissionSurfaces[keyof typeof routePermissionSurfaces], element: ReactNode) => (
  <RouteGuard surface={surface}>{element}</RouteGuard>
)

const ServiceActivationErrorHandler = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const showServiceActivation = () => navigate('/object-storage/buckets', { replace: true })
    const showDedicatedServiceActivation = () => navigate('/bare-metal', { replace: true })

    window.addEventListener(OBJECT_STORAGE_SERVICE_REQUIRED_EVENT, showServiceActivation)
    window.addEventListener(DEDICATED_SERVICE_REQUIRED_EVENT, showDedicatedServiceActivation)
    return () => {
      window.removeEventListener(OBJECT_STORAGE_SERVICE_REQUIRED_EVENT, showServiceActivation)
      window.removeEventListener(DEDICATED_SERVICE_REQUIRED_EVENT, showDedicatedServiceActivation)
    }
  }, [navigate])

  return null
}

const SidebarsRoutes = [
  {
    slug:"dashboard",
    rootPath:'home',
    component:(
      <Route path="/home" element={<HomePage />} />
    )
  },
  {
    slug:"openstack.instances",
    rootPath:'instances',
    component:(
      <>
        <Route path="/instances" element={withRouteGuard(routePermissionSurfaces.instances, <Instances />)} />
        <Route path="/gpu/*" element={withRouteGuard(routePermissionSurfaces.gpu, <GpusPage />)} />
        <Route path='/gpu/servers/create' element={withRouteGuard(routePermissionSurfaces.gpuCreate, <GpuRouteCreate />)}/>
        <Route path='/gpu/scripts/create' element={withRouteGuard(routePermissionSurfaces.gpuScriptCreate, <CreateGpuScript />)}/>
        <Route path='/gpu/scripts/edit/:scriptId' element={withRouteGuard(routePermissionSurfaces.gpuScriptEdit, <CreateGpuScript />)}/>
        <Route path='/gpu/servers/:gpuInstanceId/*' element={withRouteGuard(routePermissionSurfaces.gpuDetail, <ServerPage />)}/>
        <Route path="/instances/create" element={withRouteGuard(routePermissionSurfaces.instanceCreate, <CreateAndEditInstance />)} />
        <Route path="/instances/edit" element={withRouteGuard(routePermissionSurfaces.instanceCreate, <CreateAndEditInstance />)} />
        <Route path="/instances/:instanceId/*" element={withRouteGuard(routePermissionSurfaces.instanceDetail, <InstancePage />)} />
      </>
    )
  },
  {
    slug:"openstack.coe.clusters",
    rootPath:'kubernetes',
    component:(
      <>
        <Route path='/bare-metal' element={withRouteGuard(routePermissionSurfaces.bareMetal, <BareMetal />)}/>
        <Route path='/bare-metal/:baremetalId/*' element={withRouteGuard(routePermissionSurfaces.bareMetalDetail, <BareMetalPage />)}/>
        <Route path='/bare-metal/create' element={withRouteGuard(routePermissionSurfaces.bareMetalCreate, <CreateBareMetal />)}/>
        <Route path="/kubernetes" element={withRouteGuard(routePermissionSurfaces.kubernetes, <Kubernetes />)} />
        <Route path="/kubernetes/create" element={withRouteGuard(routePermissionSurfaces.kubernetesCreate, <CreateCluster />)} />
        <Route path="/kubernetes/:clusterId/*" element={withRouteGuard(routePermissionSurfaces.kubernetesDetail, <ClusterPage />)} />
        <Route path="/kubernetes/:clusterId/create_node_pool" element={withRouteGuard(routePermissionSurfaces.kubernetesNodePoolCreate, <CreateNodePool />)} />
      </>
    )
  },
  {
    slug:"openstack.networks",
    rootPath:'networks',
    component:(
      <>
        <Route path="/networks" element={withRouteGuard(routePermissionSurfaces.networks, <Networks />)} />
        <Route path="/networks/edit/:networkId" element={withRouteGuard(routePermissionSurfaces.networkCreate, <CreateNetwork />)} />
        <Route path="/networks/:id/*" element={withRouteGuard(routePermissionSurfaces.networkDetail, <NetworkPage />)} />
        <Route path="/networks/create" element={withRouteGuard(routePermissionSurfaces.networkCreate, <CreateNetwork />)} />
      </>
    )
  },
  {
    slug:"openstack.routers",
    rootPath:'routers',
    component:(
      <>
        <Route path="/routers" element={withRouteGuard(routePermissionSurfaces.routers, <Routers />)} />
        <Route path="/routers/:idRouter/*" element={withRouteGuard(routePermissionSurfaces.routerDetail, <RouterPage />)} />
      </>
    )
  },
  {
    slug:"openstack.ports",
    rootPath:'ports',
    component:(
      <>
        <Route path="/ports" element={withRouteGuard(routePermissionSurfaces.ports, <Ports />)} />
        <Route path="/ports/:portId" element={withRouteGuard(routePermissionSurfaces.portDetail, <PortPage />)} />
        <Route path="/ports/:portId/edit" element={withRouteGuard(routePermissionSurfaces.portEdit, <CreatePort />)} />
        <Route path="/ports/create" element={withRouteGuard(routePermissionSurfaces.portCreate, <CreatePort />)} />
      </>
    )
  },
  {
    slug:"openstack.securitygroups",
    rootPath:'security-groups',
    component:(
      <>
        <Route path="/security-groups" element={withRouteGuard(routePermissionSurfaces.securityGroups, <SecurityGroups />)} />
        <Route path="/security-groups/:groupId" element={withRouteGuard(routePermissionSurfaces.securityGroupDetail, <GroupPage />)} />
        <Route path="/security-groups/:groupId/edit" element={withRouteGuard(routePermissionSurfaces.securityGroupEdit, <CreateSecurityGroup />)} />
        <Route path="/security-groups/:groupId/create-rule" element={withRouteGuard(routePermissionSurfaces.securityGroupRuleCreate, <CreateRule />)} />
        <Route path="/security-groups/create" element={withRouteGuard(routePermissionSurfaces.securityGroupCreate, <CreateSecurityGroup />)} />
      </>
    )
  },
  {
    slug:"openstack.floatingips",
    rootPath:'floating-ips',
    component:(
      <>
        <Route path="/floating-ips" element={withRouteGuard(routePermissionSurfaces.floatingIps, <FloatingIPs />)} />
        <Route path="/floating-ips/create" element={withRouteGuard(routePermissionSurfaces.floatingIpCreate, <CreateFloatingIp />)} />
      </>
    )
  },
  {
    slug:"openstack.sshkeys",
    rootPath:'ssh-keys',
    component:(
      <>
        <Route path="/ssh-keys" element={withRouteGuard(routePermissionSurfaces.sshKeys, <SSHKeys />)} />
        <Route path="/ssh-keys/create" element={withRouteGuard(routePermissionSurfaces.sshKeyCreate, <CreateSSH />)} />
        <Route path="/ssh-keys/edit/:sshId" element={withRouteGuard(routePermissionSurfaces.sshKeyEdit, <EditSSh />)} />
        <Route path="/ssh-keys/import" element={withRouteGuard(routePermissionSurfaces.sshKeyImport, <ImportSSHKey />)} />
      </>
    )
  },
  {
    slug:"openstack.volumes",
    rootPath:'volumes',
    component:(
      <>
        <Route path="/volumes" element={withRouteGuard(routePermissionSurfaces.volumes, <Volumes />)} />
        <Route path="/volumes/:volumeId/*" element={withRouteGuard(routePermissionSurfaces.volumeDetail, <VolumePage />)} />
        <Route path="/volumes/create" element={withRouteGuard(routePermissionSurfaces.volumeCreate, <CreateAndEditVolume />)} />
      </>
    )
  },
  {
    slug:"openstack.volumes.backups",
    rootPath:'volume-backups',
    component:(
      <>
        <Route path="/volume-backups" element={withRouteGuard(routePermissionSurfaces.volumeBackups, <VolumeBackups />)} />
        <Route path="/volume-backups/:backupId" element={withRouteGuard(routePermissionSurfaces.volumeBackupDetail, <BackupPage />)} />
      </>
    )
  },
  {
    slug:"openstack.images",
    rootPath:'images',
    component:(
      <>
        <Route path="/images" element={withRouteGuard(routePermissionSurfaces.images, <Images />)} />
        <Route path="/images/:imageId" element={withRouteGuard(routePermissionSurfaces.imageDetail, <ImagePage />)} />
        <Route path="/images/:imageId/edit/*" element={withRouteGuard(routePermissionSurfaces.imageEdit, <EditImage />)} />
        <Route path="/images/create" element={withRouteGuard(routePermissionSurfaces.imageCreate, <CreateAndEditImage />)} />
        {/* ToDO: move-to key for object-store */}
        {localStorage.getItem('region_ocp_zone') === 'pl-1' || localStorage.getItem('region_ocp_zone') === 'nl-1' ? null :
          <>
            <Route path='/object-storage/*' element={withRouteGuard(routePermissionSurfaces.objectStorage, <ObjectStoragePage/>)} />
            <Route path='/object-storage/create' element={withRouteGuard(routePermissionSurfaces.objectStorageCreate, <CreateBucket/>)} />
            <Route path='/object-storage/buckets/:bucketId/create-lifecycle-rule' element={withRouteGuard(routePermissionSurfaces.objectStorageLifecycleRule, <CreateBucketRule/>)} />
            <Route path='/object-storage/buckets/:bucketId/create-policy' element={withRouteGuard(routePermissionSurfaces.objectStorageBucketPolicy, <CreateBucketPolicy/>)} />
            <Route path="/object-storage/buckets/:bucketId/*" element={withRouteGuard(routePermissionSurfaces.objectStorageBucketDetail, <BucketPage />)} />
            <Route path='/object-storage/buckets/:bucketId/lifecycle-rules/:ruleId/edit' element={withRouteGuard(routePermissionSurfaces.objectStorageLifecycleRule, <CreateBucketRule />)} />
          </>
        }

      </>
    )
  },
  {
    slug:"openstack.lbaas",
    rootPath:'load-balancers',
    component:(
      <>
        <Route path="/load-balancers" element={withRouteGuard(routePermissionSurfaces.loadBalancers, <LoadBalancers />)} />
        <Route path="/load-balancer-pool/:lbId/:poolId/*" element={withRouteGuard(routePermissionSurfaces.poolDetail, <LbPool />)} />
        <Route path="/load-balancer-pool/:lbId/:poolId/edit" element={withRouteGuard(routePermissionSurfaces.poolEdit, <EditPool />)} />
        {/* <Route path="/load-balancer-pool/:lbId/:poolId/*" element={<LbPool />} /> */}

        <Route path="/load-balancer-pool/:lbId/:poolId/health-monitors/create" element={withRouteGuard(routePermissionSurfaces.poolMonitorCreate, <CreateMonitor />)} />
        <Route path="/load-balancer-pool/:lbId/:poolId/health-monitors/:monitorId" element={withRouteGuard(routePermissionSurfaces.poolMonitorDetail, <MonitorPage />)} />
        <Route path="/load-balancer-pool/:lbId/:poolId/health-monitors/:monitorId/edit" element={withRouteGuard(routePermissionSurfaces.poolMonitorEdit, <CreateMonitor isEdit />)} />
        <Route path="/load-balancer-pool/:lbId/:poolId/members/create" element={withRouteGuard(routePermissionSurfaces.poolMemberCreate, <CreateMember />)} />
        <Route path="/load-balancer-pool/:lbId/:poolId/members/:memberId/edit" element={withRouteGuard(routePermissionSurfaces.poolMemberEdit, <EditMember/>)} />
        <Route path="/load-balancer-pool/:lbId/:poolId/members/:memberId" element={withRouteGuard(routePermissionSurfaces.poolMemberDetail, <MemberPage />)} />

        <Route path="/load-balancer-listener/:lbId/:listenerId/*" element={withRouteGuard(routePermissionSurfaces.listenerDetail, <LbListener />)} />
        <Route path="/load-balancer-listener/:lbId/:listenerId/edit" element={withRouteGuard(routePermissionSurfaces.listenerEdit, <EditListener />)} />
        <Route path="/load-balancer-listener/:lbId/:listenerId/policies/:policyId/edit" element={withRouteGuard(routePermissionSurfaces.listenerPolicyEdit, <CreatePolicy isEdit />)} />
        <Route path="/load-balancer-listener/:lbId/:listenerId/policies/:policyId/*" element={withRouteGuard(routePermissionSurfaces.listenerPolicyDetail, <PolicyPage />)} />
        <Route path="/load-balancer-listener/:lbId/:listenerId/policies/:policyId/rules/create" element={withRouteGuard(routePermissionSurfaces.listenerPolicyRuleCreate, <CreatePolicyRule />)} />
        <Route path="/load-balancer-listener/:lbId/:listenerId/policies/:policyId/rules/:ruleId/edit" element={withRouteGuard(routePermissionSurfaces.listenerPolicyRuleEdit, <CreatePolicyRule isEdit/>)} />
        <Route path="/load-balancer-listener/:lbId/:listenerId/policies/:policyId/rules/:ruleId" element={withRouteGuard(routePermissionSurfaces.listenerPolicyRuleDetail, <RulePolicyPage />)} />
        
        <Route path="/load-balancer-listener/:lbId/:listenerId/policies/create" element={withRouteGuard(routePermissionSurfaces.listenerPolicyCreate, <CreatePolicy />)} />
        <Route path="/load-balancer-listener/:lbId/:listenerId/policies/:policyId" element={withRouteGuard(routePermissionSurfaces.listenerPolicyDetail, <CreatePolicy />)} />

        <Route path="/load-balancers/:lbId/*" element={withRouteGuard(routePermissionSurfaces.loadBalancerDetail, <BalancerPage />)} />
        <Route path="/load-balancers/create" element={withRouteGuard(routePermissionSurfaces.loadBalancerCreate, <CreateLoadBalancer />)} />
        <Route path="/load-balancers/create-pool/:lbId" element={withRouteGuard(routePermissionSurfaces.loadBalancerCreatePool, <CreateLoadBalancer />)} />
        <Route path="/load-balancers/create-listener/:lbId" element={withRouteGuard(routePermissionSurfaces.loadBalancerCreateListener, <CreateLoadBalancer />)} />
        <Route path="/load-balancers/edit/:lbId" element={withRouteGuard(routePermissionSurfaces.loadBalancerEdit, <EditLB />)} />
      </>
    )
  },
  {
    slug:"utils.operations",
    rootPath:'operations',
    component:(
      <>
        <Route path="/operations" element={withRouteGuard(routePermissionSurfaces.operations, <Operations />)} />
        <Route path="/operations/:operationId" element={withRouteGuard(routePermissionSurfaces.operationDetail, <OperationPage />)} />
      </>
    )
  },
  // "openstack.dns.zones",
  // "openstack.volumes.snapshots",
  
  // "openstack.apiusers",
  // "openstack.object-store",
  // "openstack.orchestration.heat-stacks",
  // "openstack.orchestration.heat-resource-types",
  // "openstack.orchestration.heat-template-versions",
  {
    slug:"billing.services",
    rootPath:'billing',
    component:(
      <>
        <Route path='/billing/services/:serviceId' element={withRouteGuard(routePermissionSurfaces.billingServiceDetail, <ServicePage />)}/>
        <Route path="/billing/*" element={withRouteGuard(routePermissionSurfaces.billing, <BillingPage />)} />
      </>
    )
  },
  {
    slug:"billing.invoices",
    rootPath:'billing',
    component:(
      <>
        <Route path='/billing/invoices/:invoiceId' element={withRouteGuard(routePermissionSurfaces.billingInvoiceDetail, <InvoicePage />)}/>
        <Route path="/billing/*" element={withRouteGuard(routePermissionSurfaces.billing, <BillingPage />)} />
      </>
    )
  },
  // {
  //   slug:"billing.history",
  //   rootPath:'billing',
  //   component:(
  //     <>
  //       <Route path="/billing/*" element={<BillingPage />} />
  //       <Route path='/billing/invoices/:invoiceId' element={<InvoicePage />}/>
  //       <Route path='/billing/services/:serviceId' element={<ServicePage />}/>
  //     </>
  //   )
  // },
  // 
  // 
  // "billing.order",
  // "billing.order",
  // 
  
]


const AuthorizedRouter:FC = () => {
  const {smallBar} = useUi()
  return (
    <>
      <SideBar />
      <PageWrapper isSmall={smallBar}>
        <ServiceActivationErrorHandler />
        <Header/>
        <Routes>
          <Route path="/" element={<Navigate replace to="/home" />} />
          <Route path="/invitation" element={<Navigate replace to="/home" />} />
          {/* <Route path="/sign-in" element={<Navigate replace to="/home" />} /> */}
          {/* <Route path='/confirm-email/:token' element={<ConfirmEmail />} /> */}
          {/* <Route path='/invitations/:id/:token' element={<InviteAuthPage/>}/> */}
          <Route path='*' element={<NotFound />} />
          <Route path='/not-found' element={<NotFound />} />
          <Route path='/403' element={<AccessDenied />} />
          <Route path='/quotas' element={withRouteGuard(routePermissionSurfaces.quotas, <Quotas />)} />
          <Route path='/complete-registration' element={<CompleteRegistration/>}/>
          <Route path="/home" element={<HomePage />} />
          <Route path="/affiliate/*" element={<Affiliate />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/support/create/*" element={<CreateTicket />} />
          <Route path="/support/:ticketId" element={<TicketPage />} />
          <Route path="/account-settings/*" element={withRouteGuard(routePermissionSurfaces.accountSettings, <AccountSettings />)} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/notifications/:notificationId" element={<NotificationPage />} />
          <Route path="/clients" element={withRouteGuard(routePermissionSurfaces.clients, <Clients />)} />
          <Route path="/clients/:id/*" element={withRouteGuard(routePermissionSurfaces.clientDetail, <ClientPage />)} />
          <Route path='/clients/:id/client-edit' element={withRouteGuard(routePermissionSurfaces.clientEdit, <EditClient/>)} />
          {/* Создаем структуру маршрутизации на основе отфильтрованных данных */}
          {SidebarsRoutes.map((route:any) => route.component)}
          {/* ??? */}
          
        </Routes>
      </PageWrapper>
    </>
  )
}

export default AuthorizedRouter
